import React, { useState } from "react";
import { Star, Quote, Check } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import SectionCTA from "@/components/SectionCTA";
import api from "@/lib/api";
import { REVIEWS, IMG } from "@/data/site";

export default function Reviews() {
  const [form, setForm] = useState({ name: "", email: "", rating: 5, message: "" });
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) { toast.error("Please add your name and review."); return; }
    try {
      await api.post("/enquiries", { kind: "contact", name: form.name, email: form.email || "no-email@rossiceramics.co.uk", message: `REVIEW (${form.rating}★): ${form.message}`, payload: { type: "review", rating: form.rating } });
      setDone(true);
      toast.success("Thank you for your review!");
    } catch (err) { toast.error("Could not submit. Please try again."); }
  };

  return (
    <div data-testid="reviews-page">
      <Seo title="Customer Reviews" description="Read reviews and share your experience with Rossi Ceramics tiling and bathroom installation in Stockport and the North West." />
      <PageHero eyebrow="Your feedback" title="Customer reviews" subtitle="Genuine feedback matters. Share your experience or read what customers say." image={IMG.bath[1]} />

      <section className="container-rossi py-16">
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {REVIEWS.map((r, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div className="card-lux p-7 h-full">
                <Quote size={26} className="text-rossi-gold/50" />
                <div className="flex gap-1 my-3">{[...Array(5)].map((_, s) => <Star key={s} size={15} className="text-rossi-gold fill-rossi-gold" />)}</div>
                <p className="text-sm text-rossi-cream/80 italic leading-relaxed">“{r.text}”</p>
                <p className="mt-4 text-rossi-cream font-medium">{r.name}</p>
                <p className="text-xs text-rossi-stone">{r.area}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="max-w-xl mx-auto card-lux p-7">
          {done ? (
            <div className="py-12 text-center" data-testid="review-done"><Check size={40} className="text-rossi-gold mx-auto" /><h3 className="font-serif text-2xl text-rossi-cream mt-4">Thank you!</h3><p className="text-sm text-rossi-cream/70 mt-2">Your review has been sent to Rossi Ceramics.</p></div>
          ) : (
            <form onSubmit={submit}>
              <h2 className="font-serif text-2xl text-rossi-cream mb-5">Leave a review</h2>
              <div className="flex gap-2 mb-4">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })} data-testid={`review-star-${n}`}><Star size={26} className={n <= form.rating ? "text-rossi-gold fill-rossi-gold" : "text-rossi-stone"} /></button>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                <input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream focus:outline-none focus:border-rossi-gold/50" data-testid="review-name" />
                <input placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream focus:outline-none focus:border-rossi-gold/50" data-testid="review-email" />
              </div>
              <textarea placeholder="Tell us about your experience" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream focus:outline-none focus:border-rossi-gold/50" data-testid="review-message" />
              <button type="submit" className="btn-gold w-full mt-4" data-testid="review-submit">Submit review</button>
            </form>
          )}
        </div>
      </section>

      <SectionCTA />
    </div>
  );
}
